import type { OrderRating } from "./types";

/**
 * Extract the user's restaurant rating for an order.
 * API returns `rating` as an array of rating objects, not a number.
 */
export const extractId = (value: unknown): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null && "_id" in value) {
    const id = (value as { _id?: unknown })._id;
    if (typeof id === "string" && id) return id;
  }
  return "";
};

export const getOrderRestaurantRating = (order: {
  rating?: OrderRating[] | number;
  userRating?: number;
  restaurantRating?: number;
}): number => {
  if (typeof order.userRating === "number" && order.userRating > 0) {
    return order.userRating;
  }
  if (typeof order.restaurantRating === "number" && order.restaurantRating > 0) {
    return order.restaurantRating;
  }
  if (Array.isArray(order.rating)) {
    const restaurantRating = order.rating.find(
      (entry) => entry.ratingType === "restaurant" && !entry.isDeleted
    );
    return restaurantRating?.rating ?? 0;
  }
  if (typeof order.rating === "number") {
    return order.rating;
  }
  return 0;
};

export const getOrderDeliveryRating = (order: {
  rating?: OrderRating[] | number;
}): number => {
  if (!Array.isArray(order.rating)) return 0;
  const deliveryRating = order.rating.find(
    (entry) =>
      (entry.ratingType === "delivery-agent" ||
        entry.ratingType === "delivery") &&
      !entry.isDeleted
  );
  return deliveryRating?.rating ?? 0;
};

const addressInstructionKey = (addressId: string) =>
  `duddus:deliveryInstructions:${addressId}`;

export const persistAddressDeliveryInstructions = (
  addressId: string | undefined,
  instructions: string[] | string | undefined
) => {
  if (!addressId) return;
  const list = Array.isArray(instructions)
    ? instructions.map((item) => item.trim()).filter(Boolean)
    : String(instructions || "")
        .split(/\n|\. /)
        .map((item) => item.trim())
        .filter(Boolean);
  if (list.length === 0) {
    localStorage.removeItem(addressInstructionKey(addressId));
    return;
  }
  localStorage.setItem(addressInstructionKey(addressId), JSON.stringify(list));
};

export const getAddressDeliveryInstructions = (
  addressId?: string | null
): string[] => {
  if (!addressId) return [];
  try {
    const raw = localStorage.getItem(addressInstructionKey(addressId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((item) => String(item).trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
};

export const joinDeliveryInstructions = (instructions: string[]) =>
  instructions.map((item) => item.trim()).filter(Boolean).join(". ");

const pendingCouponKey = (restaurantId: string) =>
  `duddus:pendingCoupon:${restaurantId}`;

export const persistPendingCoupon = (restaurantId: string, coupon: unknown) => {
  if (!restaurantId) return;
  localStorage.setItem(pendingCouponKey(restaurantId), JSON.stringify(coupon));
};

export const getPendingCoupon = <T = unknown>(
  restaurantId: string
): T | null => {
  if (!restaurantId) return null;
  const raw = localStorage.getItem(pendingCouponKey(restaurantId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const consumePendingCoupon = <T = unknown>(
  restaurantId: string
): T | null => {
  const coupon = getPendingCoupon<T>(restaurantId);
  if (!restaurantId) return coupon;
  localStorage.removeItem(pendingCouponKey(restaurantId));
  return coupon;
};

/**
 * Format price to currency
 * @param {number} price - Price to format
 * @param {string} currency - Currency code (default: INR)
 * @returns {string} Formatted price
 */
export const formatPrice = (price: number, currency: string = 'INR'): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
  }).format(price);
};

/**
 * Format date to readable format
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date
 */
export const formatDate = (date: string | Date): string => {
  return new Date(date).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Validate email
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid phone
 */
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[0-9]{10}$/;
  return phoneRegex.test(phone.replace(/\D/g, ''));
};

/**
 * Debounce function to limit function calls
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(func: T, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Throttle function to limit function calls
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = <T extends (...args: any[]) => any>(func: T, limit: number) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Calculate distance between two coordinates
 * @param {number} lat1 - Latitude 1
 * @param {number} lon1 - Longitude 1
 * @param {number} lat2 - Latitude 2
 * @param {number} lon2 - Longitude 2
 * @returns {number} Distance in km
 */
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Get local storage item
 * @param {string} key - Storage key
 * @returns {any} Stored value or null
 */
export const getFromLocalStorage = (key: string): any => {
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

/**
 * Set local storage item
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 */
export const setToLocalStorage = (key: string, value: any): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error writing to localStorage:', error);
  }
};

/**
 * Remove item from local storage
 * @param {string} key - Storage key
 */
export const removeFromLocalStorage = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

export interface GeoPoint {
  lat: number;
  lng: number;
}

const isValidCoord = (lat: number, lng: number) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lng) <= 180 &&
  !(lat === 0 && lng === 0);

/** Parse GeoJSON [lng, lat] or { lat, lng } into a map point. */
export const parseGeoPoint = (input: unknown): GeoPoint | null => {
  if (input == null) return null;

  if (Array.isArray(input) && input.length >= 2) {
    const a = Number(input[0]);
    const b = Number(input[1]);
    if (isValidCoord(b, a)) return { lat: b, lng: a };
    if (isValidCoord(a, b)) return { lat: a, lng: b };
    return null;
  }

  if (typeof input !== "object") return null;
  const obj = input as Record<string, unknown>;

  const lat = Number(obj.lat ?? obj.latitude);
  const lng = Number(obj.lng ?? obj.longitude ?? obj.lon);
  if (isValidCoord(lat, lng)) return { lat, lng };

  if (obj.coordinates != null) return parseGeoPoint(obj.coordinates);
  if (obj.geoLocation != null) return parseGeoPoint(obj.geoLocation);
  if (obj.location != null) return parseGeoPoint(obj.location);

  return null;
};
